/**
 * Builds a concise team + roster context string for the Coach Steve AI agent.
 * Uses live app data so the agent can answer lineup, position, and attribute questions.
 */

import type { Team, Sport } from '../../types/core';
import type { PlayerCard } from '../../types/playerCard';
import { supabase } from '../../lib/supabase';
import { getCalculatedStats, formatCalculatedStat } from '../../utils/statCalculations';
import { INJURY_STATUS_LABELS } from '../../constants/injuries';
import { SPORT_STAT_KEYS } from '../../data/sportStats';

interface PlayerRecord {
  id: string;
  first_name: string;
  last_name: string;
  number?: number;
  position?: string;
  height?: string;
  weight?: number;
  speed?: number;
  strength?: number;
  experience?: string;
  injury_status?: string;
  batting_hand?: string;
  batting_average?: number;
  on_base_percentage?: number;
}

function formatBattingStat(val: number | undefined): string {
  if (val == null) return '—';
  return (typeof val === 'number' ? val.toFixed(3) : String(val)).replace(/^0/, '.');
}

/**
 * Build a single roster line for one player (with optional card data).
 */
function formatPlayerLine(
  player: PlayerRecord,
  card?: PlayerCard | null
): string {
  const num = player.number ?? '—';
  const name = `${player.first_name} ${player.last_name}`.trim() || 'Unknown';
  const pos = player.position || '—';
  const hw = [player.height, player.weight].filter(Boolean).join('/') || '—';
  const speed = player.speed != null ? String(player.speed) : '—';
  const str = player.strength != null ? String(player.strength) : '—';
  const exp = player.experience || '—';
  const injuryCode = player.injury_status || 'H';
  const injuryLabel = INJURY_STATUS_LABELS[injuryCode as keyof typeof INJURY_STATUS_LABELS] ?? injuryCode;
  const injury = `${injuryCode} (${injuryLabel})`;

  let line = `[#${num}] ${name} | pos: ${pos} | H/W: ${hw} | speed ${speed} str ${str} | exp: ${exp} | injury: ${injury}`;

  if (card) {
    const eligible = (card.eligible_positions && card.eligible_positions.length)
      ? card.eligible_positions.join(', ')
      : '—';
    const off = card.offense_designation || '—';
    const def = card.defense_designation || '—';
    const ovr = card.ovr_rating != null ? String(card.ovr_rating) : '—';
    line += `\n  Eligible: ${eligible} | Off: ${off} Def: ${def} | OVR: ${ovr}`;
  }

  const batHand = (player as any).batting_hand;
  const ba = (player as any).batting_average;
  const obp = (player as any).on_base_percentage;
  if (batHand || ba != null || obp != null) {
    line += `\n  Bat: ${batHand || '—'} BA ${formatBattingStat(ba)} OBP ${formatBattingStat(obp)}`;
  }

  return line;
}

/**
 * Build full team context string for the agent system prompt.
 * Pass in the current team and either:
 * - cardsWithPlayers: array of PlayerCard (each with .player) from getPlayerCardsForTeam, or
 * - playersOnly: array of Player from a direct roster fetch when no cards exist.
 */
export function buildTeamContextForAgent(
  team: Team,
  options: {
    cardsWithPlayers?: (PlayerCard & { player?: PlayerRecord })[];
    playersOnly?: PlayerRecord[];
  }
): string {
  const { cardsWithPlayers = [], playersOnly = [] } = options;
  const hasCards = cardsWithPlayers.length > 0;
  const roster = hasCards
    ? cardsWithPlayers
        .filter((c) => c.player)
        .map((c) => ({ player: c.player!, card: c }))
    : playersOnly.map((p) => ({ player: p, card: null }));

  if (roster.length === 0) {
    return `Team: ${team.name} | Sport: ${team.sport} | Age: ${team.ageLevel ?? '—'}\nRoster: No players in the app yet. Suggest they add players in Roster or Player Cards.`;
  }

  const settingsParts = [
    `Team: ${team.name}`,
    `Sport: ${team.sport}`,
    team.ageLevel ? `Age: ${team.ageLevel}` : '',
    team.experienceLevel ? `Level: ${team.experienceLevel}` : '',
    team.format ? `Format: ${team.format}` : '',
    team.innings ? `Innings per game: ${team.innings}` : '',
    team.offensiveScheme ? `Offense: ${team.offensiveScheme}` : '',
    team.defensiveScheme ? `Defense: ${team.defensiveScheme}` : '',
  ].filter(Boolean);

  const header = settingsParts.join(' | ');

  // Stat formula note for sports where innings drive ERA calculation
  const eraNote = (team.sport === 'Baseball' || team.sport === 'Softball') && team.innings
    ? `ERA formula for this team: (Earned Runs × ${team.innings}) / Innings Pitched (normalised to ${team.innings}-inning games, not 9).`
    : '';

  const playerLines = roster.map(({ player, card }) =>
    formatPlayerLine(player as PlayerRecord, card ?? undefined)
  );

  return [
    '--- LIVE TEAM DATA (from the app; use this to answer questions about lineups, positions, stats, and player attributes) ---',
    'Injury codes: H = Healthy, Q = Questionable, D = Doubtful, O = Out. When counting "injured" players, count only Q, D, O — never H (Healthy).',
    eraNote || null,
    header,
    `Roster (${roster.length} players):`,
    ...playerLines,
    '---',
  ].filter(Boolean).join('\n');
}

/**
 * Fetch the team's game results (scores, W/L/T) from the `games` table and return a
 * context string so the agent can answer questions about win/loss record, average runs
 * scored, runs allowed, etc.
 */
export async function buildGameResultsContextForAgent(
  teamId: string,
  sport: Sport,
): Promise<string | null> {
  try {
    const { data: games, error } = await supabase
      .from('games')
      .select('id, date, opponent_name, home_away, our_score, opponent_score')
      .eq('team_id', teamId)
      .order('date', { ascending: true });

    if (error || !games?.length) return null;

    const played = games.filter(
      (g) => g.our_score != null && g.opponent_score != null,
    );

    if (played.length === 0) return null;

    const totalOurs = played.reduce((sum, g) => sum + (g.our_score ?? 0), 0);
    const totalOpp = played.reduce((sum, g) => sum + (g.opponent_score ?? 0), 0);
    const wins = played.filter((g) => (g.our_score ?? 0) > (g.opponent_score ?? 0)).length;
    const losses = played.filter((g) => (g.our_score ?? 0) < (g.opponent_score ?? 0)).length;
    const ties = played.length - wins - losses;
    const avgOurs = (totalOurs / played.length).toFixed(2);
    const avgOpp = (totalOpp / played.length).toFixed(2);

    const scoreLabel = ['Baseball', 'Softball'].includes(sport) ? 'Runs' : 'Points';
    const tieStr = ties > 0 ? `-${ties}T` : '';
    const summary = `Record: ${wins}-${losses}${tieStr} | ${scoreLabel} scored: ${totalOurs} (avg ${avgOurs}/game) | ${scoreLabel} allowed: ${totalOpp} (avg ${avgOpp}/game)`;

    const gameLines = played.map((g) => {
      const result =
        (g.our_score ?? 0) > (g.opponent_score ?? 0)
          ? 'W'
          : (g.our_score ?? 0) < (g.opponent_score ?? 0)
          ? 'L'
          : 'T';
      return `${g.date} vs ${g.opponent_name} (${g.home_away}): ${result} ${g.our_score}-${g.opponent_score}`;
    });

    return [
      `--- GAME RESULTS — ${sport} (${played.length} game${played.length !== 1 ? 's' : ''} with scores recorded) ---`,
      summary,
      ...gameLines,
      '---',
    ].join('\n');
  } catch {
    return null;
  }
}

/**
 * Fetch game stats (sport_stats with game_id set) for the team and return a context string
 * so the agent can answer questions about player performance across all sports.
 * Uses SPORT_STAT_KEYS as the authoritative key list — automatically covers all sports
 * and picks up new stats as they are added to the app.
 */
export async function buildGameStatsContextForAgent(
  teamId: string,
  sport: Sport,
  cardsWithPlayers: (PlayerCard & { player?: { id: string; first_name: string; last_name: string; number?: number } })[],
  innings?: number
): Promise<string | null> {
  const sportStatKeys = SPORT_STAT_KEYS[sport];
  if (!sportStatKeys?.length) return null;

  try {
    // Use the server-side aggregate RPC to avoid the PostgREST 1000-row default
    // limit (a full season has 7k+ raw rows).
    const { data: rows, error } = await supabase
      .rpc('get_team_stat_totals', { p_team_id: teamId });

    if (error || !rows?.length) return null;

    // The RPC already returns one row per (player_card_id, stat_key) with the
    // season total, so we just need to group by card.
    const byCard = new Map<string, Array<{ stat_key: string; stat_value: number }>>();
    for (const row of rows) {
      const cardId = row.player_card_id as string;
      if (!byCard.has(cardId)) byCard.set(cardId, []);
      byCard.get(cardId)!.push({ stat_key: row.stat_key as string, stat_value: Number(row.total_value) });
    }

    const cardToPlayer = new Map<string, { name: string; number?: number }>();
    for (const c of cardsWithPlayers) {
      if (c.player)
        cardToPlayer.set(c.id, {
          name: `${c.player.first_name} ${c.player.last_name}`.trim(),
          number: c.player.number,
        });
    }

    const lines: string[] = [];
    for (const [playerCardId, rawEntries] of byCard) {
      const player = cardToPlayer.get(playerCardId);
      if (!player) continue;

      // Sum raw values by key
      const raw: Record<string, number> = {};
      for (const entry of rawEntries) {
        raw[entry.stat_key] = (raw[entry.stat_key] ?? 0) + entry.stat_value;
      }

      // Calculated stats (AVG/OBP/SLG/OPS for baseball; FG%/FT% for basketball; etc.)
      const calculated = getCalculatedStats(sport, rawEntries, innings);

      const parts: string[] = [];

      // Emit calculated stats first (derived metrics like AVG, ERA, FG%)
      for (const [calcKey, calcVal] of Object.entries(calculated)) {
        if (calcVal != null) {
          parts.push(`${calcKey} ${formatCalculatedStat(calcVal, calcKey)}`);
        }
      }

      // Emit every raw stat key defined for this sport that has recorded data
      for (const { key, label } of sportStatKeys) {
        if (raw[key] != null) {
          // Use the friendly label in parentheses so the AI understands what the abbreviation means
          parts.push(`${key}(${label.replace(/ \(.*\)$/, '')}) ${raw[key]}`);
        }
      }

      if (parts.length === 0) continue;

      const num = player.number != null ? ` #${player.number}` : '';
      lines.push(`${player.name}${num}: ${parts.join(' | ')}`);
    }

    if (lines.length === 0) return null;

    return [
      `--- GAME PERFORMANCE — ${sport} (season totals across all recorded games; use to answer any question about player stats) ---`,
      ...lines,
      '---',
    ].join('\n');
  } catch {
    return null;
  }
}
